import { useState, useEffect, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system' | 'public-media';
export type ColorTheme = 
  | 'zinc'
  | 'blue'
  | 'green'
  | 'orange'
  | 'rose'
  | 'violet'
  | 'teal'
  | 'amber'
  | 'indigo'
  | 'cyan'
  | 'lime'
  | 'pink'
  | 'brown';
export type Density = 'comfortable' | 'compact' | 'crowded';

const STORAGE_KEYS = {
  MODE: 'theme-mode',
  COLOR: 'theme-color',
  DENSITY: 'theme-density',
} as const;

/**
 * Custom hook for managing theme state with localStorage persistence
 * and system preference detection.
 *
 * Manages three independent theme dimensions:
 * - Mode: light / dark / system
 * - Color: zinc / blue / green / orange / rose / violet / teal / amber / indigo / cyan / lime / pink / brown
 * - Density: comfortable / compact / crowded
 */
export function useTheme() {
  // Initialize state from localStorage or defaults
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.MODE);
    return (stored as ThemeMode) || 'system';
  });

  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.COLOR);
    return (stored as ColorTheme) || 'blue';
  });

  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.DENSITY);
    return (stored as Density) || 'comfortable';
  });

  // Track the actual applied mode (light, dark, or public-media)
  const [appliedMode, setAppliedMode] = useState<'light' | 'dark' | 'public-media'>('light');

  /**
   * Get the system preference for dark mode
   */
  const getSystemPreference = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  /**
   * Determine what mode to actually apply based on user selection
   */
  const resolveMode = useCallback((selectedMode: ThemeMode): 'light' | 'dark' | 'public-media' => {
    if (selectedMode === 'system') {
      return getSystemPreference();
    }
    return selectedMode as 'light' | 'dark' | 'public-media';
  }, [getSystemPreference]);

  /**
   * Apply theme to document element
   */


  const applyTheme = useCallback((m: ThemeMode, color: ColorTheme, d: Density) => {
    const root = document.documentElement;
    const resolved = resolveMode(m);

    // Apply mode classes
    root.classList.remove('dark', 'public-media');
    if (resolved === 'dark') {
      root.classList.add('dark');
    } else if (resolved === 'public-media') {
      root.classList.add('public-media');
    }

    // Apply color theme (data-theme attribute)
    root.setAttribute('data-theme', color);

    // Apply density (theme-compact/theme-crowded class)
    root.classList.remove('theme-compact', 'theme-crowded');
    if (d === 'compact') {
      root.classList.add('theme-compact');
    } else if (d === 'crowded') {
      root.classList.add('theme-crowded');
    }

    setAppliedMode(resolved);
  }, [resolveMode]);

  // Set mode with localStorage persistence
  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEYS.MODE, newMode);
    applyTheme(newMode, colorTheme, density);
  };

  // Set color theme with localStorage persistence
  const setColorTheme = (newColor: ColorTheme) => {
    setColorThemeState(newColor);
    localStorage.setItem(STORAGE_KEYS.COLOR, newColor);
    applyTheme(mode, newColor, density);
  };

  // Set density with localStorage persistence
  const setDensity = (newDensity: Density) => {
    setDensityState(newDensity);
    localStorage.setItem(STORAGE_KEYS.DENSITY, newDensity);
    applyTheme(mode, colorTheme, newDensity);
  };

  // Apply theme on mount and when preferences change
  useEffect(() => {
    applyTheme(mode, colorTheme, density);
  }, [applyTheme, mode, colorTheme, density]);

  // Listen for system preference changes when mode is 'system'
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      applyTheme(mode, colorTheme, density);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [applyTheme, mode, colorTheme, density]);

  return {
    mode,
    setMode,
    colorTheme,
    setColorTheme,
    density,
    setDensity,
    appliedMode, // The actual mode being displayed (light or dark)
  };
}
